import { useRef, useState, useEffect } from "react"
import { useTranslation } from "react-i18next"

import wsClient from "src/wsClient"
import { useDispatch, useSelector } from "react-redux"
import { selectGameConfig } from "src/store/common"
import { pingCity } from "src/store/ui"
import ModalService from "src/modals"

import { chatInkFor } from "src/utils/chatInk"

import "./Chat.css"

/* What a line says, as text, whatever arrived (security audit, 2026-10-25). The server refuses a
   message that is not a string now, but a line is the one thing on this screen another person
   wrote: an object or a number from an older server, or a patched client, made `.replace` throw and
   took every reader's app to the crash screen. A courtesy behind the server's gate (seam rule 3). */
export const chatText = (value) => (value === null || value === undefined ? "" : String(value))

/* `people` is who has a seat in this room — the game's players, or the lobby's members — and is
   what gives each speaker a colour of their own (requirement 35, C2). The channel stays readable
   by the ` · ALL` tag and the line's left border (C4). */
const Chat = ({ chatKey, messages, title, placeholder, linkCoords = false, people = [] }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [message, setMessage] = useState("")
  const chatTitle = title ?? t("chat.title")
  const chatPlaceholder = placeholder ?? t("chat.placeholder")
  // The server's `MAX_CHAT_MESSAGE_LENGTH`: a longer line is refused (`ChatMessageTooLong`), so the
  // box stops there instead of letting somebody type a paragraph the server will turn down. "/all "
  // rides on top of it, which is why the box allows those five characters more.
  const maxLength = useSelector(selectGameConfig)?.rules_config?.MAX_CHAT_MESSAGE_LENGTH

  // A shared battle report ("⚔ … (R263)") is an object, not only a line: the 🔎 opens the battle
  // view on that battle's final round — the report, the board — for whoever has visibility of it.
  // Only the site and the round number travel in the text; fog answers the rest on the reader's
  // side (the battle view looks the closing event up in the stored steps by that round).
  const reportButton = (text) => {
    if (!linkCoords || !/^⚔/.test(String(text))) return null
    const round = /\(R(\d+)\)/.exec(text)
    const coords = /(\d{1,3})-(\d{1,3})/.exec(text)
    if (!round || !coords) return null
    return (
      <button
        type="button"
        className="chat-coord chat-report"
        onClick={() =>
          ModalService.show("BattleModal", {
            city: { col: Number(coords[1]), row: Number(coords[2]) },
            tick: Number(round[1]),
          })
        }
      >
        🔎 {t("chat.openReport")}
      </button>
    )
  }

  // "66-52" anywhere in a game chat line is a place: click it and the map travels there and puts
  // the cell under the action bar. Shared pins and hand-typed coordinates work alike.
  const renderContent = (text) => {
    if (!linkCoords) return text
    const parts = String(text).split(/(\b\d{1,3}-\d{1,3}\b)/g)
    return parts.map((part, index) => {
      const match = /^(\d{1,3})-(\d{1,3})$/.exec(part)
      if (!match) return part
      return (
        <button
          key={index}
          type="button"
          className="chat-coord"
          onClick={() => dispatch(pingCity({ col: Number(match[1]), row: Number(match[2]) }))}
        >
          {part}
        </button>
      )
    })
  }

  // A seat colour when the speaker can be placed; otherwise the channel class decides (C5).
  const inkStyle = (line) => {
    const ink = chatInkFor(line.userId, people, line.username)
    return ink ? { color: ink } : undefined
  }

  const handleSendMessage = (e) => {
    e.preventDefault()
    // One thread for both channels; the ALL tag and the line's border say which one a line went to. Team is
    // the default, and "/all " at the start sends the line to everyone.
    let realMessage = message
    let messageType = "team"
    if (message.startsWith("/all ")) {
      realMessage = message.slice(5)
      messageType = "all"
    }
    const sent = wsClient.send(chatKey, {
      action: "send_chat_message",
      message: realMessage,
      message_type: messageType,
    })
    // Keep what they typed if it never left. Clearing the box on a dropped socket loses the message
    // and looks exactly like sending it.
    if (sent) setMessage("")
  }

  /* Follow the conversation by scrolling the message list itself, never by asking the browser to
     bring an element into view. `scrollIntoView` walks *every* scrollable ancestor, and on the
     phone shell the whole lobby is one scrolling column with the chat at the bottom of it — so
     this effect, which fires once on mount with no messages at all, opened the lobby scrolled
     1,025px down at the chat box. The roster is first in that column precisely because taking a
     side is the first thing a player has to do, and this scrolled straight past it.

     In a dock the two are indistinguishable, which is why it survived: there the list's own
     scroller is the only one above it. */
  const listRef = useRef(null)
  useEffect(() => {
    const list = listRef.current
    if (!list) return
    list.scrollTo({ top: list.scrollHeight, behavior: "smooth" })
  }, [messages])

  return (
    <div className="chat-container">
      <div className="chat-header">
        <h3>{chatTitle}</h3>
        <span className="chat-hint">{t("chat.allHint")}</span>
      </div>
      <div className="chat-messages with-scroll-bar vertical" id="chat-messages" ref={listRef}>
        {messages.map((message, index) => (
          <div key={index} className={`chat-message chat-message-${message.messageType}`}>
            {message.messageType !== "system" ? (
              <span
                className={`chat-username chat-username-${message.messageType}`}
                style={inkStyle(message)}
              >
                {chatText(message.username)}
                {message.messageType === "all" ? (
                  <span className="chat-channel-tag">{` · ${t("chat.allTag")}`}</span>
                ) : null}
              </span>
            ) : (
              <span className="chat-username chat-username-system">{t("chat.system")}</span>
            )}
            <span className="chat-content">
              {renderContent(chatText(message.message).replace("/all ", ""))}
              {reportButton(chatText(message.message))}
            </span>
          </div>
        ))}
      </div>
      <form id="chat-form" className="chat-form" onSubmit={handleSendMessage}>
        <input
          type="text"
          id="chat-input"
          className="form-input"
          placeholder={chatPlaceholder}
          aria-label={t("chat.typeMessage")}
          autoComplete="off"
          value={message}
          maxLength={maxLength ? maxLength + "/all ".length : undefined}
          onChange={(e) => setMessage(e.target.value)}
        />
        <button type="submit" className="chat-send" aria-label={t("chat.sendMessage")}>
          ➤
        </button>
      </form>
    </div>
  )
}

export default Chat
